import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Cpu, CheckCircle, XCircle, Loader, MessageSquare, ArrowRight } from 'lucide-react';

/**
 * StepByStepPolicyAnalyzer - A component that allows user-controlled step-by-step 
 * analysis of policies using Claude AI, with each step running as an individual job.
 */
const StepByStepPolicyAnalyzer = ({ 
  policyId, 
  policyContent, 
  onAnalysisComplete, 
  workflowButtons = null, 
  isWorkflow = false,
  onAnalysisStart = null,
  onAnalysisEnd = null
}) => {
  // Component state
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  
  // Manual step-by-step analysis state
  const [currentStage, setCurrentStage] = useState('initial'); // initial, tactics, constitutional, precedents, excerpts, finalize, completed
  const [stageLoading, setStageLoading] = useState(false);
  
  // Intermediate results for each stage
  const [tacticsResult, setTacticsResult] = useState(null);
  const [constitutionalResult, setConstitutionalResult] = useState(null);
  const [precedentsResult, setPrecedentsResult] = useState(null);
  const [excerptsResult, setExcerptsResult] = useState(null);
  
  // Job-based async processing state
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobProgress, setJobProgress] = useState(0);
  const pollingIntervalRef = useRef(null);
  
  // Clean up polling interval on component unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);
  
  // Progress calculations
  const getProgressPercent = () => {
    if (jobProgress > 0) {
      return jobProgress;
    }
    
    switch(currentStage) {
      case 'initial': return 0;
      case 'tactics': return tacticsResult ? 20 : 0;
      case 'constitutional': return 40;
      case 'precedents': return 60;
      case 'excerpts': return 80;
      case 'finalize':
      case 'completed': return 100;
      default: return 0;
    }
  };
  
  // Stage descriptions
  const getStageDescription = (stage) => {
    switch(stage) {
      case 'initial': return "Ready to start analysis";
      case 'waiting': return "Preparing for analysis";
      case 'tactics': return "Identifying authoritarian tactics in the policy";
      case 'constitutional': return "Analyzing constitutional issues";
      case 'precedents': return "Finding relevant legal precedents";
      case 'excerpts': return "Extracting key policy excerpts";
      case 'finalize':
      case 'final': return "Finalizing analysis and generating scores";
      case 'completed': return "Analysis complete";
      case 'failed': return "Analysis failed";
      default: return "Processing policy analysis";
    }
  };
  
  // Function to start job polling
  const startPolling = (newJobId) => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // Start a new polling interval
    pollingIntervalRef.current = setInterval(() => {
      checkJobStatus(newJobId);
    }, 5000); // Poll every 5 seconds
  };
  
  // Function to check job status
  const checkJobStatus = async (jobIdToCheck) => {
    try {
      const response = await fetch(`/api/policies/job-status?job_id=${jobIdToCheck}`);
      
      const contentType = response.headers.get('content-type') || '';
      
      if (!response.ok) {
        // If it's HTML, create a more informative error
        if (contentType.includes('text/html')) {
          console.error('Received HTML error response on status check');
          return; // Don't throw - just log and continue polling
        }
        
        console.error(`Failed to check job status: ${response.statusText}`);
        return; // Continue polling despite error
      }
      
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing job status response:', parseError);
        return; // Continue polling despite error
      }
      setJobStatus(data);
      setJobProgress(data.progress || 0);
      
      // Update current stage based on job stage
      if (data.stage && data.stage !== currentStage) {
        setCurrentStage(data.stage);
      }
      
      // Store intermediate results if they're available
      if (data.stage === 'tactics' && data.has_tactics) {
        fetchStageResults(jobIdToCheck, 'tactics');
      } else if (data.stage === 'constitutional' && data.has_constitutional) {
        fetchStageResults(jobIdToCheck, 'constitutional');
      } else if (data.stage === 'precedents' && data.has_precedents) {
        fetchStageResults(jobIdToCheck, 'precedents');
      } else if (data.stage === 'excerpts' && data.has_excerpts) {
        fetchStageResults(jobIdToCheck, 'excerpts');
      }
      
      // If the job is done (completed or failed), stop polling
      if (data.status === 'completed' || data.status === 'failed') {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        
        if (data.status === 'completed') {
          // Handle completed job - process the results
          setStageLoading(false);
          setCurrentStage('completed');
          
          if (data.result) {
            processAnalysisResults(data.result);
          } else {
            // If result is not included in status, fetch it separately
            fetchFinalResults(jobIdToCheck);
          }
        } else if (data.status === 'failed') {
          // Handle failed job
          setStageLoading(false);
          setCurrentStage('failed');
          setError(data.error || 'Analysis failed. Please try again.');
          if (onAnalysisEnd) onAnalysisEnd();
        }
      }
      
    } catch (err) {
      console.error('Error checking job status:', err);
      // Don't stop polling on a single error - it might just be a temporary network issue
    }
  };
  
  // Fetch results for a specific stage
  const fetchStageResults = async (jobId, stage) => {
    try {
      // Endpoint to fetch specific stage results
      const response = await fetch(`/api/policies/job-results?job_id=${jobId}&stage=${stage}`);
      
      if (!response.ok) {
        console.error(`Error fetching ${stage} results:`, response.statusText);
        return;
      }
      
      const data = await response.json();
      
      if (stage === 'tactics' && data.tactics_result) {
        try {
          const parsedResult = JSON.parse(data.tactics_result);
          setTacticsResult(parsedResult);
        } catch (e) {
          console.error('Error parsing tactics result:', e);
        }
      } else if (stage === 'constitutional' && data.constitutional_result) {
        try {
          const parsedResult = JSON.parse(data.constitutional_result);
          setConstitutionalResult(parsedResult);
        } catch (e) {
          console.error('Error parsing constitutional result:', e);
        }
      } else if (stage === 'precedents' && data.precedents_result) {
        try {
          const parsedResult = JSON.parse(data.precedents_result);
          setPrecedentsResult(parsedResult);
        } catch (e) {
          console.error('Error parsing precedents result:', e);
        }
      } else if (stage === 'excerpts' && data.excerpts_result) {
        try {
          const parsedResult = JSON.parse(data.excerpts_result);
          setExcerptsResult(parsedResult);
        } catch (e) {
          console.error('Error parsing excerpts result:', e);
        }
      }
    } catch (err) {
      console.error(`Error fetching ${stage} results:`, err);
    }
  };
  
  // Fetch final analysis results
  const fetchFinalResults = async (jobId) => {
    try {
      const response = await fetch(`/api/policies/job-results?job_id=${jobId}&final=true`);
      
      if (!response.ok) {
        setError('Failed to fetch final analysis results');
        return;
      }
      
      const data = await response.json();
      
      if (data.final_result) {
        try {
          const parsedResult = JSON.parse(data.final_result);
          processAnalysisResults(parsedResult);
        } catch (e) {
          console.error('Error parsing final result:', e);
          setError('Error processing analysis results');
        }
      } else {
        setError('No final analysis results available');
      }
    } catch (err) {
      console.error('Error fetching final results:', err);
      setError('Failed to fetch analysis results');
    }
  };
  
  // Process the current stage
  const processCurrentStage = async () => {
    if (stageLoading) return; // Prevent multiple simultaneous requests
    
    setStageLoading(true);
    setError(null);
    
    try {
      console.log(`Processing current stage: ${currentStage}, policy ID: ${policyId}`);
      
      // Verify policy data is available
      if (!policyId) {
        throw new Error("Policy ID is required for analysis. Please ensure the policy is properly saved.");
      }
      
      if (currentStage === 'initial') {
        // Start the tactics analysis job
        await startTacticsJob();
      }
      else if (currentStage === 'tactics' && tacticsResult) {
        // Proceed to constitutional analysis
        await startConstitutionalJob();
      }
      else if (currentStage === 'constitutional' && constitutionalResult) {
        // Proceed to precedents analysis
        await startPrecedentsJob();
      }
      else if (currentStage === 'precedents' && precedentsResult) {
        // Proceed to excerpts analysis
        await startExcerptsJob();
      }
      else if (currentStage === 'excerpts' && excerptsResult) {
        // Proceed to final analysis
        await startFinalJob();
      }
    } catch (err) {
      console.error(`Error processing ${currentStage} stage:`, err);
      setError(err.message || `Error analyzing ${currentStage} stage`);
      setStageLoading(false);
    }
  };
  
  // Start a new job for tactics analysis
  const startTacticsJob = async () => {
    try {
      if (onAnalysisStart) onAnalysisStart();
      
      // Log the content availability for debugging
      console.log(`Starting tactics analysis for policy ${policyId}. Content available: ${!!policyContent}, content length: ${policyContent ? policyContent.length : 0}`);
      
      // Submit the job
      const response = await fetch('/api/policies/analyze-staged', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          policy_id: policyId,
          stage: 'tactics'
        })
      });
      
      // Check if response is HTML (which would indicate a server error page)
      const contentType = response.headers.get('content-type') || '';
      
      if (!response.ok) {
        // If it's HTML, create a more informative error
        if (contentType.includes('text/html')) {
          const htmlContent = await response.text();
          console.error('Received HTML error response:', htmlContent.substring(0, 200) + '...');
          throw new Error(`Server returned HTML error page (Status: ${response.status}). This likely indicates a server-side issue.`);
        }
        
        // Try to parse as JSON but handle errors
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to submit tactics analysis job: ${response.statusText}`);
        } catch (parseError) {
          throw new Error(`Failed to submit tactics analysis job (Status: ${response.status}): ${response.statusText}`);
        }
      }
      
      // Handle successful response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response as JSON:', parseError);
        throw new Error('Server returned invalid JSON response. Check server logs for details.');
      }
      
      // Store the result directly
      setTacticsResult(data.analysis);
      setCurrentStage('tactics');
      setStageLoading(false);
      
    } catch (err) {
      console.error('Error starting tactics analysis:', err);
      setError(err.message || 'Failed to analyze tactics');
      setStageLoading(false);
      if (onAnalysisEnd) onAnalysisEnd();
    }
  };
  
  // Start constitutional analysis
  const startConstitutionalJob = async () => {
    try {
      // Submit the constitutional job
      const response = await fetch('/api/policies/analyze-staged', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          policy_id: policyId,
          stage: 'constitutional',
          previous_results: {
            tactics: tacticsResult
          }
        })
      });
      
      // Check if response is HTML (which would indicate a server error page)
      const contentType = response.headers.get('content-type') || '';
      
      if (!response.ok) {
        // If it's HTML, create a more informative error
        if (contentType.includes('text/html')) {
          const htmlContent = await response.text();
          console.error('Received HTML error response:', htmlContent.substring(0, 200) + '...');
          throw new Error(`Server returned HTML error page (Status: ${response.status}). This likely indicates a server-side issue.`);
        }
        
        // Try to parse as JSON but handle errors
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to submit constitutional analysis job: ${response.statusText}`);
        } catch (parseError) {
          throw new Error(`Failed to submit constitutional analysis job (Status: ${response.status}): ${response.statusText}`);
        }
      }
      
      // Handle successful response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response as JSON:', parseError);
        throw new Error('Server returned invalid JSON response. Check server logs for details.');
      }
      
      // Store the result directly
      setConstitutionalResult(data.analysis);
      setCurrentStage('constitutional');
      setStageLoading(false);
      
    } catch (err) {
      console.error('Error analyzing constitutional issues:', err);
      setError(err.message || 'Failed to analyze constitutional issues');
      setStageLoading(false);
    }
  };
  
  // Start precedents analysis
  const startPrecedentsJob = async () => {
    try {
      // Submit the precedents job
      const response = await fetch('/api/policies/analyze-staged', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          policy_id: policyId,
          stage: 'precedents',
          previous_results: {
            tactics: tacticsResult,
            constitutional: constitutionalResult
          }
        })
      });
      
      // Check if response is HTML (which would indicate a server error page)
      const contentType = response.headers.get('content-type') || '';
      
      if (!response.ok) {
        // If it's HTML, create a more informative error
        if (contentType.includes('text/html')) {
          const htmlContent = await response.text();
          console.error('Received HTML error response:', htmlContent.substring(0, 200) + '...');
          throw new Error(`Server returned HTML error page (Status: ${response.status}). This likely indicates a server-side issue.`);
        }
        
        // Try to parse as JSON but handle errors
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to submit precedents analysis job: ${response.statusText}`);
        } catch (parseError) {
          throw new Error(`Failed to submit precedents analysis job (Status: ${response.status}): ${response.statusText}`);
        }
      }
      
      // Handle successful response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response as JSON:', parseError);
        throw new Error('Server returned invalid JSON response. Check server logs for details.');
      }
      
      // Store the result directly
      setPrecedentsResult(data.analysis);
      setCurrentStage('precedents');
      setStageLoading(false);
      
    } catch (err) {
      console.error('Error analyzing legal precedents:', err);
      setError(err.message || 'Failed to analyze legal precedents');
      setStageLoading(false);
    }
  };
  
  // Start excerpts extraction
  const startExcerptsJob = async () => {
    try {
      // Submit the excerpts job
      const response = await fetch('/api/policies/analyze-staged', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          policy_id: policyId,
          stage: 'excerpts',
          previous_results: {
            tactics: tacticsResult,
            constitutional: constitutionalResult,
            precedents: precedentsResult
          }
        })
      });
      
      // Check if response is HTML (which would indicate a server error page)
      const contentType = response.headers.get('content-type') || '';
      
      if (!response.ok) {
        // If it's HTML, create a more informative error
        if (contentType.includes('text/html')) {
          const htmlContent = await response.text();
          console.error('Received HTML error response:', htmlContent.substring(0, 200) + '...');
          throw new Error(`Server returned HTML error page (Status: ${response.status}). This likely indicates a server-side issue.`);
        }
        
        // Try to parse as JSON but handle errors
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to submit excerpts extraction job: ${response.statusText}`);
        } catch (parseError) {
          throw new Error(`Failed to submit excerpts extraction job (Status: ${response.status}): ${response.statusText}`);
        }
      }
      
      // Handle successful response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response as JSON:', parseError);
        throw new Error('Server returned invalid JSON response. Check server logs for details.');
      }
      
      // Store the result directly
      setExcerptsResult(data.analysis);
      setCurrentStage('excerpts');
      setStageLoading(false);
      
    } catch (err) {
      console.error('Error extracting key excerpts:', err);
      setError(err.message || 'Failed to extract key excerpts');
      setStageLoading(false);
    }
  };
  
  // Start final analysis
  const startFinalJob = async () => {
    try {
      // Submit the finalize job
      const response = await fetch('/api/policies/analyze-staged', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          policy_id: policyId,
          stage: 'finalize',
          previous_results: {
            tactics: tacticsResult,
            constitutional: constitutionalResult,
            precedents: precedentsResult,
            excerpts: excerptsResult
          }
        })
      });
      
      // Check if response is HTML (which would indicate a server error page)
      const contentType = response.headers.get('content-type') || '';
      
      if (!response.ok) {
        // If it's HTML, create a more informative error
        if (contentType.includes('text/html')) {
          const htmlContent = await response.text();
          console.error('Received HTML error response:', htmlContent.substring(0, 200) + '...');
          throw new Error(`Server returned HTML error page (Status: ${response.status}). This likely indicates a server-side issue.`);
        }
        
        // Try to parse as JSON but handle errors
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to submit final analysis job: ${response.statusText}`);
        } catch (parseError) {
          throw new Error(`Failed to submit final analysis job (Status: ${response.status}): ${response.statusText}`);
        }
      }
      
      // Handle successful response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response as JSON:', parseError);
        throw new Error('Server returned invalid JSON response. Check server logs for details.');
      }
      
      // Process the final analysis results
      processAnalysisResults(data.analysis);
      setCurrentStage('completed');
      setStageLoading(false);
      
    } catch (err) {
      console.error('Error finalizing analysis:', err);
      setError(err.message || 'Failed to finalize analysis');
      setStageLoading(false);
    }
  };
  
  // Process the final analysis results
  const processAnalysisResults = async (finalResult) => {
    // Store the full result
    setResult(finalResult);
    
    try {
      // Update policy status to 'analyzed'
      if (policyId) {
        await fetch(`/api/policies/${policyId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: 'analyzed'
          })
        });
      }
    } catch (err) {
      console.error('Error updating policy status after analysis:', err);
    }
    
    // Notify parent component of completion with analysis data
    if (onAnalysisComplete) {
      onAnalysisComplete({
        analysisData: finalResult
      });
    } else if (onAnalysisEnd) {
      onAnalysisEnd();
    }
  };
  
  // Reset the analysis
  const resetAnalysis = () => {
    // Clear polling interval if active
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    setCurrentStage('initial');
    setStageLoading(false);
    setTacticsResult(null);
    setConstitutionalResult(null);
    setPrecedentsResult(null);
    setExcerptsResult(null);
    setResult(null);
    setError(null);
    setJobId(null);
    setJobStatus(null);
    setJobProgress(0);
    
    if (onAnalysisEnd) onAnalysisEnd();
  };
  
  // Progress bar component
  const ProgressBar = ({ progress }) => {
    return (
      <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
        <div 
          className="bg-blue-600 h-2.5 rounded-full transition-all duration-500 ease-in-out"
          style={{ width: `${progress}%` }}
        ></div>
      </div>
    );
  };
  
  return (
    <div className="space-y-6">
      {/* Workflow buttons at the top if provided */}
      {workflowButtons && (
        <div className="mb-4">
          {workflowButtons}
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Cpu className="mr-2 h-5 w-5 text-indigo-600" />
          Step-by-Step Policy Analysis with Claude AI
        </h3>
        
        {error && (
          <div className="p-4 mb-4 bg-red-50 border-l-4 border-red-500 text-red-700">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <div>
                <p className="text-sm font-medium">{error}</p>
                
                {error.includes('timed out') && (
                  <p className="text-xs mt-2">
                    The policy appears to be too long or complex for Claude to analyze within the time limit. 
                    Try with a shorter policy or break it into sections.
                  </p>
                )}
                
                {error.includes('HTML error page') && (
                  <p className="text-xs mt-2">
                    The analysis server encountered an error. This could be because:
                    <ul className="list-disc ml-4 mt-1">
                      <li>The CLAUDE_API_KEY is not configured on the server</li>
                      <li>The policy ID may not exist in the database</li>
                      <li>There might be a server-side issue with database access</li>
                    </ul>
                    Please contact the administrator to check the server logs.
                  </p>
                )}
                
                {error.includes('invalid JSON') && (
                  <p className="text-xs mt-2">
                    The server responded with invalid data. This usually indicates a configuration issue
                    on the server or an API endpoint problem. Please check the server logs.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {currentStage === 'initial' && !stageLoading && !result && (
          <div className="bg-indigo-50 p-4 rounded-md mb-4">
            <p className="text-sm text-indigo-800 mb-3">
              Claude AI will analyze this policy in simple, sequential steps:
            </p>
            <ul className="text-sm text-indigo-800 list-disc pl-5 space-y-2">
              <li>Step 1: Identify authoritarian tactics used in the policy</li>
              <li>Step 2: Analyze constitutional issues in the policy</li>
              <li>Step 3: Identify relevant legal precedents</li>
              <li>Step 4: Extract key policy excerpts</li>
              <li>Step 5: Generate final scores and analysis</li>
            </ul>
            <p className="text-sm text-indigo-800 mt-3">
              Click the button below to start step 1, and you'll proceed through each step individually.
            </p>
            <div className="text-sm text-green-700 mt-3 bg-green-50 p-2 rounded font-medium">
              <span className="font-bold">ENHANCED STEP-BY-STEP PROCESS:</span> Each step runs as a separate job for maximum reliability.
            </div>
          </div>
        )}
        
        {/* Progress display */}
        {(stageLoading || currentStage !== 'initial' && currentStage !== 'completed') && (
          <div className={`p-4 rounded-md mb-4 ${stageLoading ? 'bg-blue-50' : 'bg-green-50'}`}>
            <div className="flex items-center mb-2">
              {stageLoading ? (
                <Loader className="h-5 w-5 text-blue-600 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              )}
              <h4 className={`font-medium ${stageLoading ? 'text-blue-800' : 'text-green-800'}`}>
                {stageLoading ? 'Processing' : 'Completed'}: {getStageDescription(currentStage)}
              </h4>
            </div>
            
            <ProgressBar progress={getProgressPercent()} />
            
            <div className={`p-2 rounded mt-2 ${stageLoading ? 'bg-blue-100' : 'bg-green-100'}`}>
              <p className={`text-xs ${stageLoading ? 'text-blue-700' : 'text-green-700'}`}>
                Current Stage: {currentStage}
              </p>
              {currentStage === 'tactics' && tacticsResult && (
                <p className="text-xs text-green-700">
                  Found {tacticsResult.tactics?.length || 0} authoritarian tactics
                </p>
              )}
              {currentStage === 'constitutional' && constitutionalResult && (
                <p className="text-xs text-green-700">
                  Identified {constitutionalResult.issues?.length || 0} constitutional issues
                </p>
              )}
              {currentStage === 'precedents' && precedentsResult && (
                <p className="text-xs text-green-700">
                  Identified {precedentsResult.precedents?.length || 0} legal precedents
                </p>
              )}
              {currentStage === 'excerpts' && excerptsResult && (
                <p className="text-xs text-green-700">
                  Extracted {excerptsResult.excerpts?.length || 0} key excerpts
                </p>
              )}
              {jobId && (
                <p className="text-xs text-green-700">
                  Job ID: {jobId}
                </p>
              )}
            </div>
          </div>
        )}
        
        {/* Results summary */}
        {currentStage === 'completed' && result && (
          <div className="mb-4">
            <div className="bg-green-50 p-4 rounded-md mb-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <h4 className="font-medium text-green-800">
                  Analysis Complete
                </h4>
              </div>
              
              <p className="text-sm text-green-700 mt-2">
                {result.overall_assessment?.summary || "Claude has analyzed the policy and identified key issues and authoritarian tactics."}
              </p>
              
              {/* Add continue button here for workflow */}
              {(workflowButtons || isWorkflow) && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => onAnalysisComplete && onAnalysisComplete({
                      analysisData: result
                    })}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none"
                  >
                    Continue to Review & Publish
                  </button>
                </div>
              )}
            </div>
            
            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="font-medium text-gray-800 mb-2">Analysis Results</h4>
              
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div className="bg-white p-3 rounded border border-gray-200">
                  <div className="text-xs text-gray-500">Authoritarian Tactics</div>
                  <div className="text-lg font-semibold">
                    {result.overall_assessment?.tactics_score || 
                     result.score_tactics || 
                     "N/A"}/100
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded border border-gray-200">
                  <div className="text-xs text-gray-500">Constitutional Issues</div>
                  <div className="text-lg font-semibold">
                    {result.overall_assessment?.constitutional_score || 
                     result.score_constitutional || 
                     "N/A"}/100
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded border border-gray-200">
                  <div className="text-xs text-gray-500">Legal Precedents</div>
                  <div className="text-lg font-semibold">
                    {result.overall_assessment?.precedents_score || 
                     result.score_precedents || 
                     "N/A"}/100
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded border border-gray-200">
                  <div className="text-xs text-gray-500">Overall Rating</div>
                  <div className="text-lg font-semibold">
                    {result.overall_assessment?.combined_score || 
                     result.score || 
                     "N/A"}/100
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-3 rounded border border-gray-200 mb-3">
                <div className="text-xs text-gray-500 mb-1">Threat Level</div>
                <div className="text-lg font-semibold">
                  {result.overall_assessment?.threat_level || 
                   (result.overall_assessment?.combined_score >= 81 ? "Severe" :
                    result.overall_assessment?.combined_score >= 61 ? "Significant" :
                    result.overall_assessment?.combined_score >= 41 ? "Moderate" :
                    result.overall_assessment?.combined_score >= 21 ? "Mild" : "Minimal") || 
                   "N/A"}
                </div>
              </div>
              
              <h5 className="font-medium text-gray-700 text-sm mb-1">Key Findings</h5>
              <div className="flex flex-wrap gap-4">
                {result.tactics && result.tactics.length > 0 && (
                  <div className="bg-white p-3 rounded border border-gray-200 flex-1 min-w-[250px]">
                    <div className="text-xs text-gray-500 mb-1">Top Authoritarian Tactics</div>
                    <ul className="text-sm space-y-1">
                      {result.tactics.slice(0, 3).map((tactic, index) => (
                        <li key={index} className="flex items-start py-1 border-t border-gray-100">
                          <div className="flex-shrink-0 mt-1">
                            <span className="inline-block h-2 w-2 rounded-full mr-2 bg-red-500"></span>
                          </div>
                          <div className="flex-1 font-medium">{tactic.name || tactic.tactic_name}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {result.constitutional_issues && result.constitutional_issues.length > 0 && (
                  <div className="bg-white p-3 rounded border border-gray-200 flex-1 min-w-[250px]">
                    <div className="text-xs text-gray-500 mb-1">Top Constitutional Issues</div>
                    <ul className="text-sm space-y-1">
                      {result.constitutional_issues.slice(0, 3).map((issue, index) => (
                        <li key={index} className="flex items-start py-1 border-t border-gray-100">
                          <div className="flex-shrink-0 mt-1">
                            <span className="inline-block h-2 w-2 rounded-full mr-2 bg-blue-500"></span>
                          </div>
                          <div className="flex-1 font-medium">{issue.name || issue.issue_name}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Action buttons */}
        <div className="flex justify-end mt-4">
          {/* Initial start button */}
          {currentStage === 'initial' && !stageLoading && !result && (
            <div>
              <button
                id="claude-analyze-step1-button"
                type="button"
                onClick={processCurrentStage}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                disabled={stageLoading}
              >
                <Cpu className="mr-2 h-4 w-4" />
                Step 1: Analyze Authoritarian Tactics
              </button>
            </div>
          )}
          
          {/* Tactics stage completed - proceed to constitutional */}
          {currentStage === 'tactics' && !stageLoading && tacticsResult && (
            <button
              type="button"
              onClick={processCurrentStage}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
              disabled={stageLoading}
            >
              <ArrowRight className="mr-2 h-4 w-4" />
              Step 2: Analyze Constitutional Issues
            </button>
          )}
          
          {/* Constitutional stage completed - proceed to precedents */}
          {currentStage === 'constitutional' && !stageLoading && constitutionalResult && (
            <button
              type="button"
              onClick={processCurrentStage}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
              disabled={stageLoading}
            >
              <ArrowRight className="mr-2 h-4 w-4" />
              Step 3: Identify Legal Precedents
            </button>
          )}
          
          {/* Precedents stage completed - proceed to excerpts */}
          {currentStage === 'precedents' && !stageLoading && precedentsResult && (
            <button
              type="button"
              onClick={processCurrentStage}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
              disabled={stageLoading}
            >
              <ArrowRight className="mr-2 h-4 w-4" />
              Step 4: Extract Key Excerpts
            </button>
          )}
          
          {/* Excerpts stage completed - proceed to finalize */}
          {currentStage === 'excerpts' && !stageLoading && excerptsResult && (
            <button
              type="button"
              onClick={processCurrentStage}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
              disabled={stageLoading}
            >
              <ArrowRight className="mr-2 h-4 w-4" />
              Step 5: Generate Final Analysis
            </button>
          )}
          
          {/* Loading state - show spinner */}
          {stageLoading && (
            <button
              type="button"
              disabled
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded shadow-sm text-gray-400 bg-gray-100"
            >
              <Loader className="animate-spin mr-2 h-4 w-4" />
              Processing...
            </button>
          )}
          
          {/* Reset button for completed analysis */}
          {currentStage === 'completed' && result && (
            <div className="flex space-x-4">
              {/* Continue button for workflow mode */}
              {(isWorkflow || workflowButtons) && (
                <button
                  type="button"
                  onClick={() => onAnalysisComplete && onAnalysisComplete({
                    analysisData: result
                  })}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none"
                >
                  Continue to Review & Publish
                </button>
              )}
              
              {/* Reset button */}
              <button
                type="button"
                onClick={resetAnalysis}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
              >
                <XCircle className="mr-2 h-4 w-4" />
                Reset Analysis
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepByStepPolicyAnalyzer;